import React, { useState, useMemo } from 'react';
import { Modal, Button, DatePicker, Checkbox, Segmented, Alert, message, Typography, Row, Col, Divider } from 'antd';
import { SyncOutlined, CalendarOutlined, FieldTimeOutlined, LockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

// --- CONFIGURAÇÃO DE TIMEZONE E LOCALE ---
import 'dayjs/locale/pt-br';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { Method_POST_CONTEXTO } from '../../../../service/api';
import { useDraggableModal } from "../Balancete/utils/modalDraggable";

// Configura plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Define locale e o timezone padrão para São Paulo
dayjs.locale('pt-br');
dayjs.tz.setDefault('America/Sao_Paulo');

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Lista atualizada
const OPTIONS_TO_REGENERATE = [
    // { label: 'Lançamentos Bancários', value: 'LANCAMENTO_BANCARIO' },
    { label: 'Pagamentos Impostos', value: 'PAGAMENTOS_IMPOSTOS' },
    { label: 'Pagamentos Notas MCO', value: 'PGTO_NOTAS_MCO' },
    { label: 'Pagamentos Pró-labore', value: 'PGTO_PRO_LABORE' },
    { label: 'Provisão Faturamento', value: 'FATURAMENTO' },
    { label: 'Provisão Folha', value: 'FOLHA' },
    { label: 'Provisão DAS', value: 'DAS' },
    { label: 'Provisão Notas MCO', value: 'NOTAS_MCO' },
    { label: 'Recebimentos cliente', value: 'RECEBIMENTOS' },
];

const ModalRegeneratePeriod = ({ show, handleClose, onUpdate, zIndex, onFocus }) => {
    const draggableProps = useDraggableModal(zIndex, onFocus, {
        show,
        modalKey: 'regerar',
        initialWidth: 650,
        initialHeight: 350,
        overflow: 'visible'
    });
    const [periodType, setPeriodType] = useState('month');
    const [dateValue, setDateValue] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    // --- LÓGICA DE BLOQUEIO COM FUSO DE SP ---
    const disabledDate = (current) => {
        // Pega a data/hora atual EXATAMENTE em São Paulo
        const nowInSP = dayjs().tz('America/Sao_Paulo');

        // Calcula 2 meses atrás baseado em SP e pega o fim daquele mês
        const maxAllowedDate = nowInSP.subtract(1, 'months').endOf('month');

        // Bloqueia se a data do calendário for maior que o limite permitido
        return current && current > maxAllowedDate;
    };

    // Feedback visual do período
    const periodDescription = useMemo(() => {
        if (!dateValue) return null;

        let start = '';
        let end = '';

        // Formatação visual continua usando o objeto do DatePicker (local), 
        // mas como enviamos YYYY-MM-DD string, o backend Java converte corretamente.
        if (periodType === 'month') {
            start = dateValue.startOf('month').format('DD/MM/YYYY');
            end = dateValue.endOf('month').format('DD/MM/YYYY');
        } else if (periodType === 'custom' && dateValue[0] && dateValue[1]) {
            start = dateValue[0].startOf('month').format('DD/MM/YYYY');
            end = dateValue[1].endOf('month').format('DD/MM/YYYY');
        } else {
            return null;
        }

        return (
            <Alert
                message={<span style={{ fontSize: '12px' }}>Serão regerados dados de <b>{start}</b> até <b>{end}</b>.</span>}
                type="info"
                showIcon
                style={{ marginTop: 8, padding: '4px 12px' }}
            />
        );
    }, [dateValue, periodType]);

    const handleRegenerate = async () => {
        if (!dateValue) {
            setErrorMsg('Por favor, selecione a data ou período.');
            return;
        }
        if (selectedOptions.length === 0) {
            setErrorMsg('Selecione pelo menos uma opção para regerar.');
            return;
        }

        setLoading(true);
        setErrorMsg(null);

        try {
            // 1. Preparar as Datas para o Body
            let dataInicialStr = '';
            let dataFinalStr = '';

            if (periodType === 'month') {
                dataInicialStr = dateValue.startOf('month').format('YYYY-MM-DD');
                dataFinalStr = dateValue.endOf('month').format('YYYY-MM-DD');
            } else if (periodType === 'custom') {
                dataInicialStr = dateValue[0].startOf('month').format('YYYY-MM-DD');
                dataFinalStr = dateValue[1].endOf('month').format('YYYY-MM-DD');
            }

            // 2. Preparar os Tipos para a URL
            const params = new URLSearchParams();
            selectedOptions.forEach(opt => {
                params.append('tipos', opt);
            });

            const baseUrl = '/empresa/contabilidade/regerar/periodo';
            const urlCompleta = `${baseUrl}?${params.toString()}`;

            // 3. Montar o Body JSON
            const payloadPesquisa = {
                dataInicio: dataInicialStr,
                dataTermino: dataFinalStr
            };

            await Method_POST_CONTEXTO(urlCompleta, payloadPesquisa);

            message.success('Regeração solicitada com sucesso!');
            if (onUpdate) onUpdate();
            handleCancel();

        } catch (error) {
            console.error(error);
            let msg = 'Erro ao processar.';
            if (error.response && error.response.data && error.response.data.message) {
                msg = error.response.data.message;
            }
            setErrorMsg(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setDateValue(null);
        setSelectedOptions([]);
        setErrorMsg(null);
        handleClose();
    };

    const toggleSelectAll = () => {
        if (selectedOptions.length === OPTIONS_TO_REGENERATE.length) {
            setSelectedOptions([]);
        } else {
            setSelectedOptions(OPTIONS_TO_REGENERATE.map(opt => opt.value));
        }
    };

    // --- TEXTO LEGÍVEL COM FUSO DE SP ---
    // Garante que o texto exibido ("Outubro/2024") seja baseado no hoje de SP
    const maxDateReadable = dayjs()
        .tz('America/Sao_Paulo')
        .subtract(2, 'months')
        .format('MMMM/YYYY');

    return (
        <Modal
            title={
                <span>
                    <SyncOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                    Regerar Lançamentos
                </span>
            }
            open={show}
            onCancel={handleCancel}
            width={650}
            {...draggableProps}
            styles={{ body: { overflow: 'hidden' } }}
            footer={[
                <Button key="back" onClick={handleCancel} disabled={loading}>
                    Cancelar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleRegenerate}
                    disabled={!dateValue || selectedOptions.length === 0}
                >
                    Confirmar Regeração
                </Button>,
            ]}
        >
            {errorMsg && (
                <Alert message="Erro" description={errorMsg} type="error" showIcon style={{ marginBottom: 16 }} />
            )}

            <div style={{ marginTop: 12 }}>

                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, color: '#faad14' }}>
                    <LockOutlined />
                    <span style={{ fontSize: '12px' }}>
                        Por segurança, meses recentes estão bloqueados. Permitido até <b style={{ textTransform: 'capitalize' }}>{maxDateReadable}</b>.
                    </span>
                </div>

                <div style={{ backgroundColor: '#f9f9f9', padding: 16, borderRadius: 8, border: '1px solid #f0f0f0' }}>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>1. Definição do Período</Text>

                    <Segmented
                        block
                        options={[
                            { label: 'Mensal', value: 'month', icon: <CalendarOutlined /> },
                            { label: 'Personalizado', value: 'custom', icon: <FieldTimeOutlined /> },
                        ]}
                        value={periodType}
                        onChange={(val) => {
                            setPeriodType(val);
                            setDateValue(null);
                        }}
                        style={{ marginBottom: 12 }}
                    />

                    {periodType === 'custom' ? (
                        <RangePicker
                            style={{ width: '100%' }}
                            picker="month"
                            format="MM/YYYY"
                            value={dateValue}
                            onChange={(val) => setDateValue(val)}
                            disabledDate={disabledDate}
                            placeholder={['Mês Início', 'Mês Fim']}
                        />
                    ) : (
                        <DatePicker
                            picker="month"
                            style={{ width: '100%' }}
                            placeholder="Selecione o Mês"
                            format="MM/YYYY"
                            value={dateValue}
                            onChange={(val) => setDateValue(val)}
                            disabledDate={disabledDate}
                        />
                    )}

                    {periodDescription}
                </div>

                <Divider style={{ margin: '20px 0' }} />

                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text strong>2. Itens para Processamento</Text>
                        <Button type="link" size="small" onClick={toggleSelectAll} style={{ padding: 0 }}>
                            {selectedOptions.length === OPTIONS_TO_REGENERATE.length ? 'Desmarcar todos' : 'Marcar todos'}
                        </Button>
                    </div>

                    <Checkbox.Group style={{ width: '100%' }} value={selectedOptions} onChange={setSelectedOptions}>
                        <Row gutter={[16, 12]}>
                            {OPTIONS_TO_REGENERATE.map((opt) => (
                                <Col span={12} key={opt.value}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <Checkbox value={opt.value}>
                                            {opt.label}
                                        </Checkbox>
                                        {opt.obs && (
                                            <span style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: 24 }}>
                                                {opt.obs}
                                            </span>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Checkbox.Group>
                </div>
            </div>
        </Modal>
    );
};

export default ModalRegeneratePeriod;